import * as React from "react";

import { cn, cva } from "../../lib";
import { SwitchPrimitive } from "../../primitives/switch";
import type { SwitchProps } from "./types";

// How far the knob (and its native halo) moves from the content edge: 3px
// from the track's edge at sm and md, 4px at lg, off and on
// (`getSwitchKnobOffset`)
const knobTranslates = [
  { size: "sm", checked: false, class: "translate-x-switch-knob-off-sm" },
  { size: "md", checked: false, class: "translate-x-switch-knob-off-md" },
  { size: "lg", checked: false, class: "translate-x-switch-knob-off-lg" },
  { size: "sm", checked: true, class: "translate-x-switch-knob-on-sm" },
  { size: "md", checked: true, class: "translate-x-switch-knob-on-md" },
  { size: "lg", checked: true, class: "translate-x-switch-knob-on-lg" },
] as const;

const switchVariants = cva(
  "web:focus-visible:ring-luce-brand-primaryBorder web:focus-visible:ring-focus web:focus-visible:ring-offset-focus rounded-radius-full group peer inline-flex shrink-0 cursor-pointer flex-row items-center border-2 outline-none transition-colors",
  {
    variants: {
      size: {
        sm: "h-switch-sm w-switch-sm",
        md: "h-switch-md w-switch-md",
        lg: "h-switch-lg w-switch-lg",
      },
      checked: {
        true: "bg-luce-brand-primaryHover border-luce-brand-primaryHover/100",
        false:
          "bg-luce-neutral-foreground-attenuated border-luce-neutral-foreground-attenuated/100",
      },
      disabled: {
        true: "pointer-events-none cursor-not-allowed",
        false: "",
      },
    },
    compoundVariants: [
      {
        disabled: true,
        checked: false,
        class: "bg-luce-button-cta-disabled border-luce-button-cta-disabled/100",
      },
      {
        disabled: true,
        checked: true,
        class: "bg-luce-brand-primary border-luce-brand-primary/100 opacity-disabled",
      },
    ],
    defaultVariants: {
      size: "md" as const,
      checked: false,
      disabled: false,
    },
  },
);

// The knob sits 3px in from the track's edge (4px at lg), off and on (Figma);
// it starts at the content edge, inside the 2px border. On web the halo is a
// ring, on hover and while pressed; native draws `switchHaloVariants`.
const switchThumbVariants = cva(
  "web:ring-luce-brand-primary/20 web:ring-0 web:group-hover:ring-switch-halo web:group-active:ring-switch-halo shadow-knob rounded-radius-full pointer-events-none block transition-transform",
  {
    variants: {
      size: {
        sm: "size-switch-knob-sm",
        md: "size-switch-knob-md",
        lg: "size-switch-knob-lg",
      },
      checked: {
        true: "bg-luce-brand-onPrimary",
        false: "bg-luce-neutral-foreground-inverse",
      },
      disabled: {
        true: "",
        false: "",
      },
    },
    compoundVariants: [
      ...knobTranslates,
      // Disabled unchecked thumb color
      { disabled: true, checked: false, class: "bg-luce-button-cta-onCtaDisabled" },
    ],
    defaultVariants: {
      size: "md" as const,
      checked: false,
      disabled: false,
    },
  },
);

/**
 * Native: the pressed halo, a brand/primary 20% disc 7px around the knob
 * (Figma "Hovered / Pressed"), drawn beneath it and moved with it. The Root is
 * a Pressable with `group`, so it shows while a finger is down.
 */
const switchHaloVariants = cva(
  "bg-luce-brand-primary/20 rounded-radius-full absolute -left-[7px] opacity-0 group-active:opacity-100",
  {
    variants: {
      // The knob plus 7px each side, centred on it in the 16/20/28px content box
      size: {
        sm: "-top-[6px] size-[28px]",
        md: "-top-[6px] size-[32px]",
        lg: "-top-[5px] size-[38px]",
      },
      checked: {
        true: "",
        false: "",
      },
    },
    compoundVariants: [...knobTranslates],
    defaultVariants: {
      size: "md" as const,
      checked: false,
    },
  },
);

/**
 * Switch atom — applies Luce design (CVA) to the Switch primitive.
 * Single file, multiplatform via primitive resolution.
 */
const Switch = React.forwardRef<React.ComponentRef<typeof SwitchPrimitive>, SwitchProps>(
  ({ checked, onCheckedChange, disabled = false, size = "md", className, ...rest }, ref) => {
    const rootClasses = cn(switchVariants({ size, checked, disabled }), className);
    const thumbClasses = cn(switchThumbVariants({ size, checked, disabled }));
    // A disabled switch takes no press, so it has no halo
    const haloClasses = disabled ? undefined : switchHaloVariants({ size, checked });
    return (
      <SwitchPrimitive
        ref={ref}
        checked={checked}
        onCheckedChange={onCheckedChange}
        disabled={disabled}
        className={rootClasses}
        thumbClassName={thumbClasses}
        haloClassName={haloClasses}
        {...rest}
      />
    );
  },
);

Switch.displayName = "Switch";

export { Switch };
